import {
  GAME_BOARD_SIZE,
  HEX_LINE_WIDTH,
  DIR_WIDTH,
  COMPUTER_USERID,
} from "../constants_v2.js";
import { action } from "./action_v2.js";
import { preferences } from "../preferences_v2.js";
import { zoom } from "../zoom_v2.js";
import { scoreBoard } from "../scoreBoard/scoreBoard_v2.js";
import {
  isAnimationRunning,
  animatePiece,
  setAnimationRunning,
  animateRotation,
  animateRemoval,
} from "./animations_v2.js";
import {
  calculateFillColor,
  coordInPieces,
  isHoveringHexagon,
  isAvailable,
  isPushAvailable,
  isRemoveAvailable,
  isRotateAvailable,
  isDouble,
  hoverColor,
  figureOutS,
  gameCoordState,
  getNeighbor,
  gameCoords2Pixels,
  getHexagonVertices,
  pixels2gameCoords,
  drawCenterText,
  getHighestScore,
} from "./utils-game_v2.js";
import { utilsApp } from "../utils-app_v2.js";
import { colors } from "../colors_v2.js";
import { matchService } from "../matches/matchService_v2.js";
import { identityService } from "../profile/identityService_v2.js";
import { matchDraw } from "../matches/matchDraw_v2.js";
import { resign } from "../matches/matchResign_v2.js";
import { cancel } from "../matches/cancelMatch_v2.js";
import { rilgorithm } from "./rilgorithm_v2.js";
import { endGamePop } from "../game/endGamePop_v2.js";
import { eventHub } from "../eventHub_v2.js";
import { dasModal } from "../modal_v2.js";
import { currentMatch } from "../matches/currentMatch_v2.js";
import { recentMatches } from "../matches/recentMatches_v2.js";
import { mouse } from "./mouse_v2.js";

export var gameState = {
  hexagons: [],
  hoveringHexagon: { s: -5, q: 3, r: 2 }, // default to a center piece
  highlight: {
    state: false,
    coords: {},
  },
  rewindOffset: 0,
};

const canvas = document.getElementById("canvas");
const ctx = canvas.getContext("2d");

const getRewindOffset = () => {
  return gameState.rewindOffset;
};

const renderStateSubSection = (stateValue) => {
  const stateElement = document.getElementById("stateSubSection");
  stateElement.classList.add("pillSubSection", "courier");
  stateElement.style.backgroundColor = colors.getGameColor("double");
  if (typeof stateValue === "undefined") {
    stateValue = "n/a";
  }
  stateValue = matchService.getFriendlyState(stateValue);
  stateElement.textContent = stateValue.toUpperCase();
};

const renderPASubSection = (pAName) => {
  const pAElement = document.getElementById("pASubSection");
  pAElement.classList.add("pillSubSection", "courier");
  pAElement.style.backgroundColor = colors.getGameColor("double");
  if (typeof pAName === "undefined") {
    pAName = "n/a";
  }
  pAElement.textContent = pAName.toUpperCase();
};

const renderWatcherNavCount = (match) => {
  const watchers = match?.watchers;
  const active = match?.active ?? "inActive";
  let count = 0;
  if (Array.isArray(watchers)) {
    count = watchers.length;
  }
  const currentMatchNav = document.getElementById("currentMatchNav");
  if (count === 0 || typeof count != "number" || active === "inActive") {
    currentMatchNav.innerText = "Current";
    return;
  }
  if (count >= 0) {
    currentMatchNav.innerText = "Current (" + count.toString() + ")";
    return;
  }
};

const renderMatchCount = async () => {
  const recentMatchesNav = document.getElementById("recentMatchesNav");
  const count = await recentMatches.numMatches();
  if (count === 0 || typeof count != "number") {
    recentMatchesNav.innerText = "Recent";
    return;
  }
  if (count >= 0) {
    recentMatchesNav.innerText = "Recent (" + count.toString() + ")";
    return;
  }
};

const handleFastForward = async () => {
  const currentMatch = JSON.parse(
    JSON.stringify(matchService.getCurrentMatch().currentMatch),
  );
  const moves = currentMatch?.moves;
  if (!Array.isArray(moves)) {
    return;
  }
  if (gameState.rewindOffset < 0) {
    gameState.rewindOffset++;
    const reWoundMoveAfter = currentMatch.moves.at(gameState.rewindOffset - 1);
    drawHexagonOnGrid({
      coords: reWoundMoveAfter.coords,
      borderColor: colors.getGameColor("iPop"),
      fillColor: colors.getGameColor("iPop"),
    });
    await delay(1000);

    drawTheGrid();
    scoreBoard.drawRewindScores(reWoundMoveAfter.pieces);
    renderMoveSelectors(currentMatch);
  }

  if (gameState.rewindOffset === 0) {
    await matchService.refreshCurrentMatch();
    render();
  }
};

const handleRewind = async () => {
  const currentMatch = matchService.getCurrentMatch().currentMatch;
  const moves = currentMatch?.moves;
  if (!Array.isArray(moves)) {
    return;
  }

  if (gameState.rewindOffset > (moves.length - 1) * -1) {
    const reWoundMove = currentMatch.moves.at(gameState.rewindOffset - 1);
    drawHexagonOnGrid({
      coords: reWoundMove.coords,
      borderColor: colors.getGameColor("iPop"),
      fillColor: colors.getGameColor("iPop"),
    });
    await delay(1000);

    gameState.rewindOffset--;
    drawTheGrid();
    scoreBoard.drawRewindScores(
      currentMatch.moves.at(gameState.rewindOffset - 1).pieces,
    );
    renderMoveSelectors(currentMatch);
  }
};

const renderMoveSelectors = async (match) => {
  const rewindSection = document.getElementById("rewindSection");
  const moves = match?.moves ?? [];
  const numMoves = moves.length;
  const html = `
  <div id="moveSelectorContainer" class="flexRow">
    <div><button id="rewind" class="rewFF">«</button></div>
    <div id="currentMove" class="noWrap">Move ${numMoves + gameState.rewindOffset} of ${numMoves}</div>
    <div><button id="fastForward" class="rewFF">»</button></div>

  `;
  rewindSection.innerHTML = html;
  colors.setButtonColors();
  const currentMove = document.getElementById("currentMove");
  if (gameState.rewindOffset < 0) {
    currentMove.style.backgroundColor = colors.getGameColor("iPop");
    currentMove.style.fontWeight = "600";
  } else {
    currentMove.style.backgroundColor = "none";
    currentMove.style.fontWeight = "100";
  }
};

/**
 * @typedef {object} coords
 * @property {string} s - The s axis (downLeft through upRight )
 * @property {string} q - The q axis (vertical)
 * @property {string} r - The r axis ( upLeft through downRight )
 * ```
 *    +s   +r
 * -q   Hex  +q
 *    -r   +s
 * ```
 * * s axis (upLeft through downRight, increments up left, decrements down right)
 * * q axis (horizontal axis, 0 is the left most, increments to the right)
 * * r axis (downLeft through upRight, decrements up right, increments down left)
 */

/**
 * @brief Renders static UI elements.
 * @description Updates colors, zoom controls, and the settings gear icon
 * that do not depend on the current match state.
 */
const initializeStaticUI = () => {
  setColors();
  zoom.render();
  preferences.renderGear();
};

/**
 * @brief Updates global match-related UI components.
 * @description Renders the player identity, match state, watcher counts, and total match count.
 * @param {Object} match - The current match data object.
 * @param {string} identityName - The name of the player currently "playing as."
 */
const updateGlobalMatchUI = (match, identityName) => {
  renderPASubSection(identityName);
  renderStateSubSection(match?.state);
  renderWatcherNavCount(match);
  renderMatchCount();
};

/**
 * @brief Fetches and validates the current user identity.
 * @description Communicates with the identity service to determine who the user is playing as.
 * @async
 * @returns {Promise<Object|null>} Returns an object with name and userID, or null if the session is invalid.
 */
const fetchIdentityContext = async () => {
  const response = await identityService.getPlayingAs();
  if (response.status !== 200) return null;

  const identity = await response.identity;
  return {
    name: identity?.name ?? "n/a",
    userID: identity?.userID,
  };
};

/**
 * @brief Synchronizes match data with the server.
 * @description Fetches the current match and triggers a refresh if the move history is missing.
 * @async
 * @returns {Promise<Object|null>} The match object if successful, otherwise null.
 */
const syncMatchData = async () => {
  const result = await matchService.getCurrentMatch();
  if (result.status !== 200) return null;

  const match = result.currentMatch;
  // Refresh if data is stale (missing moves)
  if (!match?.moves) {
    await matchService.refreshCurrentMatch();
  }
  return match;
};

/**
 * @brief Cleans up the UI when no active match exists.
 * @description Clears the canvas and resets the action section to a blank state.
 * @async
 */
const handleNoActiveMatch = async () => {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  await action.clearSection();
};

/**
 * @brief Prepares the UI for an active match session.
 * @description Resets rotation, sets default player actions, and reveals the canvas container.
 * @async
 */
const prepareMatchUI = async () => {
  document.getElementById("canvasContainer").classList.remove("hide");

  matchService.setForceRotate(false);
  await matchService.setAction({ action: "push", player: "playerOne" });
  await matchService.setAction({ action: "push", player: "playerTwo" });

  action.renderSection();
  action.renderSelector();
};

/**
 * @brief Routes the rendering logic based on the specific match state.
 * @description Handles the branching logic for game statuses like started, draw offered, or finished.
 * @async
 * @param {Object} match - The current match object.
 * @param {string} pAUserID - The ID of the identity currently being used.
 * @param {boolean} pAisPlayer - Flag indicating if the current identity is a participant in the match.
 */
const renderMatchByState = async (match, pAUserID, pAisPlayer) => {
  renderMoveSelectors(match);

  switch (match.state) {
    case "pending":
    case "started":
    case "finished":
      await renderGrid();
      break;

    case "drawDeclined":
      await handleDrawDeclinedState(match, pAUserID, pAisPlayer);
      break;

    case "drawOffered":
      await handleDrawOfferedState(match, pAUserID, pAisPlayer);
      break;

    case "draw":
      await handleDrawAcceptedState(match, pAisPlayer);
      break;

    case "notifyCancel":
      await handleNotificationState(
        match,
        pAUserID,
        pAisPlayer,
        cancel.handleGotCancelNotification,
      );
      break;

    case "notifyResign":
      await handleNotificationState(
        match,
        pAUserID,
        pAisPlayer,
        resign.handleGotResignNotification,
      );
      break;
  }
};

/**
 * @brief Generic handler for terminal or notification-based states (Cancel/Resign).
 * @description Determines if a notification modal should be shown based on turn and device mode.
 * @async
 * @param {Object} match - The current match object.
 * @param {string} pAUserID - The current user's ID.
 * @param {boolean} pAisPlayer - Whether the user is a player.
 * @param {Function} notificationHandler - The specific service function to trigger the notification.
 */
const handleNotificationState = async (
  match,
  pAUserID,
  pAisPlayer,
  notificationHandler,
) => {
  if (!pAisPlayer) return;

  await renderGrid();

  const isMyTurn = pAUserID === match.turn;
  const isOwnDevice = match.mode === "ownDevice";

  // If it's not my turn or we're on the same device, trigger notification
  if (!isMyTurn || !isOwnDevice) {
    notificationHandler();
  }
};

/**
 * @brief Logic for handling a match that has ended in a draw.
 * @description Updates the grid and displays the appropriate modal message for players and watchers.
 * @async
 * @param {Object} match - The current match object.
 * @param {boolean} pAisPlayer - Whether the user is a player.
 */
const handleDrawAcceptedState = async (match, pAisPlayer) => {
  await renderGrid();
  dasModal.close();

  if (!pAisPlayer) {
    dasModal.render("<div class=max>This match has ended as a draw</div>");
    return;
  }

  if (currentMatch.getSize() === "normal") {
    currentMatch.render("small");
    dasModal.render("<div class=max>The draw was accepted</div>");
  }
};
/**
 * @brief Main entry point for the rendering pipeline.
 * @description Coordinates identity checks, data synchronization, and state-based UI updates.
 * @async
 */
const render = async () => {
  initializeStaticUI();

  const identityData = await fetchIdentityContext();
  if (!identityData) return; // Session expired

  const match = await syncMatchData();
  updateGlobalMatchUI(match, identityData.name);

  if (!match) {
    handleNoActiveMatch();
    // Also hide chat when no match
    const { chat } = await import("../chat/chat_v2.js");
    chat.hide();
    return;
  }

  prepareMatchUI();

  // Update chat for current match
  const { chat } = await import("../chat/chat_v2.js");
  await chat.updateForMatch(match);

  const pAisPlayer = await matchService.pAIsPlayer();
  await renderMatchByState(match, identityData.userID, pAisPlayer);
};

/**
 * @brief Handles the UI and modal state when a draw has been offered.
 * @description Manages the grid rendering and the "decide draw" prompt.
 * Logic branches based on whether the match is on a shared device or
 * if the current user is the one who needs to respond to the offer.
 * @async
 * @param {Object} match - The current match data object.
 * @param {string} pAUserID - The ID of the identity currently playing.
 * @param {boolean} pAisPlayer - Flag indicating if the user is a participant.
 */
const handleDrawOfferedState = async (match, pAUserID, pAisPlayer) => {
  const isOwnDevice = match.mode === "ownDevice";
  const isSameDevice = match.mode === "sameDevice";
  const isMyTurn = pAUserID === match.turn;

  if (isOwnDevice) {
    await renderGrid();
  }

  // Determine if this user needs to accept/decline.
  // Scenario A: It's a private device, you are a player, and it's NOT your turn.
  const needsToDecideOwnDevice = pAisPlayer && isOwnDevice && !isMyTurn;

  // It's a shared device and you are a player.
  const needsToDecideSameDevice = pAisPlayer && isSameDevice;

  if (needsToDecideOwnDevice || needsToDecideSameDevice) {
    await matchDraw.handleDecideDraw();
  }

  // dasModal.render will not override an existing modal (like the decide pop-up),
  // so this acts as the default message for watchers or the player who offered.
  dasModal.render("<div class=max>A draw has been offered</div>");
};

/**
 * @brief Manages the UI state when a draw offer has been rejected.
 * @description Handles board rendering for private devices and triggers
 * the 'declined' notification logic for the appropriate player or watcher.
 * @async
 * @param {Object} match - The current match data object.
 * @param {string} pAUserID - The ID of the identity currently playing.
 * @param {boolean} pAisPlayer - Flag indicating if the user is a participant.
 */
const handleDrawDeclinedState = async (match, pAUserID, pAisPlayer) => {
  const isOwnDevice = match.mode === "ownDevice";
  const isSameDevice = match.mode === "sameDevice";
  const isMyTurn = pAUserID === match.turn;

  //  Always show the board state on own devices.
  if (isOwnDevice) {
    await renderGrid();
  }

  // Trigger the 'declined' handler for the person who needs to see it.
  // This runs if they are on a shared device, OR if it's their turn on a own device
  // (meaning they were the ones who offered the draw and it was rejected).
  if (isSameDevice || (isOwnDevice && isMyTurn)) {
    matchDraw.handleDrawDeclined();
  }

  // If a non-player is watching on a own device,
  // ensure they get a clear status update.
  if (isOwnDevice && !pAisPlayer) {
    dasModal.close();
    dasModal.render("<div class=max>The draw was declined</div>");
  }
};

// const handleComputerTurn = async () => {
//   const currentMatchResult = matchService.getCurrentMatch();
//   if (currentMatchResult.status != 200) {
//     return;
//   }
//   const currentMatch = currentMatchResult.currentMatch;
//   const bestMove = rilgorithm.bestMove();

//   board.setHighlightCoords(bestMove.piece.coords);
//   board.setHighlightState(true);
//   renderGrid();

//   await delay(1000);
//   board.setHighlightState(false);

//   const computerPlayer =
//     currentMatch.p1UserID === COMPUTER_USERID ? "playerOne" : "playerTwo";
//   await matchService.setAction({
//     action: bestMove.action,
//     player: computerPlayer,
//   });
//   matchService.setDirection({
//     direction: bestMove.piece.direction,
//     player: computerPlayer,
//   });
//   await takeAction({
//     coords: bestMove.piece.coords,
//     player: computerPlayer,
//   });
// };

// const setHighlightState = (boolean) => {
//   if (typeof boolean != "boolean") {
//     return;
//   }
//   gameState.highlight.state = boolean;
// };

// const setHighlightCoords = (coords) => {
//   if (matchService.isCoordsValid === false) {
//     return;
//   }
//   gameState.highlight.coords = coords;
// };

// const setColors = () => {
//   document.getElementById("canvas").style.borderColor =
//     colors.getGameColor("messageBackground");
// };

/**
 * @brief Manages the automated turn for the computer opponent.
 * @description Retrieves the optimal move from the algorithm, provides visual
 * feedback via highlighting, and executes the final action.
 * @async
 */
const handleComputerTurn = async () => {
  const matchResult = matchService.getCurrentMatch();
  if (matchResult.status !== 200) return;

  // 1. Calculate Best Move
  const match = matchResult.currentMatch;
  const bestMove = rilgorithm.bestMove();
  const computerKey =
    match.p1UserID === COMPUTER_USERID ? "playerOne" : "playerTwo";

  // 2. Visual Feedback (The "Thinking" Pause)
  await showComputerHighlight(bestMove.piece.coords);

  // 3. Execute Action
  await executeComputerMove(computerKey, bestMove);
};

/**
 * @brief Briefly highlights a hex to show which piece the computer is moving.
 * @async
 * @param {Object} coords - The hex coordinates to highlight.
 */
const showComputerHighlight = async (coords) => {
  setHighlightCoords(coords);
  setHighlightState(true);
  await renderGrid();

  await delay(1000); // Give the player time to see what's happening

  setHighlightState(false);
  // No need to renderGrid again here, the move execution will trigger a re-render
};

/**
 * @brief Sets the internal state and triggers the move action for the AI.
 * @async
 */
const executeComputerMove = async (playerKey, move) => {
  await matchService.setAction({
    action: move.action,
    player: playerKey,
  });

  matchService.setDirection({
    direction: move.piece.direction,
    player: playerKey,
  });

  await takeAction({
    coords: move.piece.coords,
    player: playerKey,
  });
};

/**
 * @brief Toggles the visual highlight state on the board.
 * @param {boolean} isActive - Whether the highlight should be visible.
 */
const setHighlightState = (isActive) => {
  if (typeof isActive !== "boolean") return;
  gameState.highlight.state = isActive;
};

/**
 * @brief Sets the coordinates for the board highlight.
 * @param {Object} coords - The game coordinates to target.
 */
const setHighlightCoords = (coords) => {
  // Fixed logic: actually calling the validation service
  if (!matchService.isCoordsValid(coords)) return;

  gameState.highlight.coords = coords;
};

/**
 * @brief Updates the canvas border to match the current theme.
 */
const setColors = () => {
  const themeColor = colors.getGameColor("messageBackground");
  document.getElementById("canvas").style.borderColor = themeColor;
};

/**
 * @brief Coordinates the multi-step process of moving game pieces.
 * @description Handles animation setup, path calculation, execution, and state finalization.
 * @async
 * @param {Object} params - The movement parameters.
 * @param {Array} params.movePlan - The initial array of movement steps.
 * @param {string} params.player - The player performing the move.
 * @param {boolean} params.animateOnly - If true, skips turn-changing logic.
 * @param {Object} params.matchReceived - The match data to cache if in animateOnly mode.
 */
const movePieces = async ({ movePlan, player, animateOnly, matchReceived }) => {
  setAnimationRunning(true);

  // Prepare path and save temporary UI state
  const rememberHoveringHexagon = pauseHoverState();
  const fullPath = extendPathWithNeighbor(movePlan);

  //  Run the animations and track total duration
  const totalAnimDuration = await executePieceAnimations(fullPath);

  // Wait for animations to finish, then commit changes
  // Adding 30ms buffer to ensure visual smoothness
  await new Promise((resolve) => setTimeout(resolve, totalAnimDuration + 30));

  await finalizeMove({
    player,
    animateOnly,
    matchReceived,
    rememberHoveringHexagon,
  });
};

/**
 * @brief Temporarily disables hex highlighting during animation.
 * @returns {Object} The previous hovering state to be restored later.
 */
const pauseHoverState = () => {
  const previous = gameState.hoveringHexagon;
  gameState.hoveringHexagon = { s: -2, q: -2, r: -2 };
  return previous;
};

/**
 * @brief Calculates the final destination hex and appends it to the path.
 * @param {Array} movePlan - The original sequence of moves.
 * @returns {Array} The augmented path including the final neighbor hex.
 */
const extendPathWithNeighbor = (movePlan) => {
  const lastStep = movePlan[movePlan.length - 1];
  const neighbor = getNeighbor({
    coords: lastStep.coords,
    direction: lastStep.direction,
  });
  return [...movePlan, neighbor];
};

/**
 * @brief Iterates through the path and triggers individual piece animations.
 * @async
 * @param {Array} path - The full list of coordinates for the move.
 * @returns {Promise<number>} The total accumulated delay in milliseconds.
 */
const executePieceAnimations = async (path) => {
  let animDelayMs = 0;
  const isAnimationOff = identityService.getAnimationMode() === "Off";

  for (let i = 0; i <= path.length - 2; i++) {
    const from = {
      piece: path[i],
      index: coordInPieces({
        coords: path[i].coords,
        piecesArray: matchService.getPieces().pieces,
      }),
      cx: gameCoords2Pixels(path[i].coords).cx,
      cy: gameCoords2Pixels(path[i].coords).cy,
    };

    const to = {
      piece: path[i + 1],
      cx: gameCoords2Pixels(path[i + 1].coords).cx,
      cy: gameCoords2Pixels(path[i + 1].coords).cy,
    };

    if (isAnimationOff) animDelayMs = 0;

    animDelayMs = await animatePiece({
      i,
      animDelayMs,
      pieces2Move: path,
      from,
      to,
    });
  }
  return animDelayMs;
};

/**
 * @brief Commits the move to the match service and restores UI state.
 * @async
 * @param {Object} config - Finalization settings including player and UI state.
 */
const finalizeMove = async ({
  player,
  animateOnly,
  matchReceived,
  rememberHoveringHexagon,
}) => {
  // Store the new piece location (usually set in animCache by animatePiece)
  await matchService.addPiece({ coords: gameState.animCache, player });
  setAnimationRunning(false);

  if (animateOnly) {
    handleAnimateOnlyCompletion(matchReceived);
    return;
  }

  const changeTurnResult = await matchService.changeTurn(gameState.animCache);
  if (changeTurnResult.status !== 200) {
    setUXAfterError();
    return;
  }

  // Cleanup
  delete gameState.animCache;
  if (gameState.hoveringHexagon.s === -2) {
    gameState.hoveringHexagon = rememberHoveringHexagon;
  }

  await renderGrid();
};

/**
 * @brief Specific cleanup for 'Animate Only' mode (e.g., match updates from server).
 * @param {Object} matchReceived - The match data to update in local cache.
 */
const handleAnimateOnlyCompletion = (matchReceived) => {
  console.log("handleAnimateOnlyCompletion: Updating cache with match: " + matchReceived.matchID);
  console.log("handleAnimateOnlyCompletion: Match state is: " + matchReceived.state);
  console.log("handleAnimateOnlyCompletion: Match pieces length is: " + (matchReceived.pieces ? matchReceived.pieces.length : "undefined"));
  console.log("handleAnimateOnlyCompletion: Match turn is: " + matchReceived.turn);
  matchService.updateMatchInCache(matchReceived);
  board.render();
  mouse.clearDownEventInProgress();
};

const setUXAfterError = () => {
  dasModal.close();
  dasModal.renderNoX(`
    
    <p>Sorry about this</p>
    Something went wrong. Please refresh your browser.
    <ul style="text-align:left; min-width:300px;">
      <li>If you were logged in, log in again after refreshing. It will probably still be your turn.</li>
      <li>If you weren't logged in, this match can't be recovered.</li>
    </ul>
    `);
};

/**
 * @brief Manages the removal of a piece from the board.
 * @description Validates the removal intent and branches between
 * a cache-only update (for animations/subscriptions) or a full database transaction.
 * @async
 * @param {Object} params - The removal parameters.
 * @param {Object} params.coords - Target hex coordinates.
 * @param {boolean} [params.animateOnly] - Flag for skipping database writes.
 * @param {Object} [params.matchReceived] - Contextual match data for cache updates.
 */
const handleRemove = async ({ coords, animateOnly, matchReceived }) => {
  const turnAsPlayer = matchService.getTurn().player;
  const removeResult = await isRemoveAvailable({ coords, turnAsPlayer });

  if (!removeResult.available) return;

  // Trigger the animation first
  await animateRemoval({
    coords,
    player: gameCoordState(coords).player,
  });

  if (animateOnly) {
    handleAnimateOnlyRemoval(matchReceived);
    return;
  }

  await executePieceRemoval(coords);
};

/**
 * @brief Executes the removal transaction and advances the game turn.
 * @async
 * @param {Object} coords - The coordinates of the piece to remove.
 */
const executePieceRemoval = async (coords) => {
  await matchService.removePiece(coords);

  const changeTurnResult = await matchService.changeTurn(coords);
  if (changeTurnResult.status !== 200) {
    setUXAfterError();
    return;
  }

  await renderGrid();
};

/**
 * @brief Finalizes a removal for subscription-based updates.
 * @param {Object} matchReceived - The match object to be cached.
 */
const handleAnimateOnlyRemoval = (matchReceived) => {
  matchService.updateMatchInCache(matchReceived);
  board.render();
  mouse.clearDownEventInProgress();
};

/**
 * @brief Manages the logic for adding a piece via a 'push' action.
 * @description Validates push availability and branches between an
 * animated multi-piece move or an immediate single-piece placement.
 * @async
 * @param {Object} params - The push parameters.
 * @param {Object} params.coords - Targeted hex coordinates.
 * @param {string} params.player - The active player.
 * @param {boolean} params.animateOnly - Flag for skipping database writes.
 * @param {Object} params.matchReceived - The match data for cache updates.
 */
const handlePush = async ({ coords, player, animateOnly, matchReceived }) => {
  const pushResult = isPushAvailable(coords);

  if (!pushResult.available) return;

  // Path A: The move requires shifting other pieces (Animation)
  if (pushResult.movePlan.length > 0) {
    await executeAnimatedPush({
      movePlan: pushResult.movePlan,
      coords,
      player,
      animateOnly,
      matchReceived,
    });
    return;
  }

  // Path B: Simple placement with no shifting
  await executeImmediatePlacement({
    coords,
    player,
    animateOnly,
    matchReceived,
  });
};

/**
 * @brief Executes a push that involves moving multiple pieces.
 * @description Caches the starting coordinates and triggers the movement pipeline.
 * @async
 */
const executeAnimatedPush = async ({
  movePlan,
  coords,
  player,
  animateOnly,
  matchReceived,
}) => {
  gameState.animCache = coords;

  await movePieces({
    movePlan,
    player,
    animateOnly,
    matchReceived,
  });
};

/**
 * @brief Handles piece placement when no other pieces need to be moved.
 * @description Branches between a simple UI/Cache update or a full turn-changing database write.
 * @async
 */
const executeImmediatePlacement = async ({
  coords,
  player,
  animateOnly,
  matchReceived,
}) => {
  if (animateOnly) {
    handleAnimateOnlyPlacement(matchReceived);
    return;
  }

  await matchService.addPiece({ coords, player });

  const changeTurnResult = await matchService.changeTurn(coords);
  if (changeTurnResult.status !== 200) {
    setUXAfterError();
    return;
  }

  await renderGrid();
};

/**
 * @brief Finalizes a non-animated placement for subscription-based updates.
 * @param {Object} matchReceived - The match object to be cached.
 */
const handleAnimateOnlyPlacement = (matchReceived) => {
  matchService.updateMatchInCache(matchReceived);
  board.render();
  mouse.clearDownEventInProgress();
};

/**
 * @brief Executes the rotation of a piece on the board.
 * @description Validates the rotation intent, updates the piece direction,
 * and manages turn transitions for both live play and remote syncs.
 * @async
 * @param {Object} params - The rotation parameters.
 */
const handleRotate = async ({ coords, player, animateOnly, matchReceived }) => {
  const rotateResult = isRotateAvailable({ coords, turnAsPlayer: player });
  if (!rotateResult.available) return;

  const pieces = matchService.getPieces().pieces;
  const targetIndex = coordInPieces({ coords, piecesArray: pieces });
  const chosenDirection = matchService.getDirection(player);

  // 1. Validation Guards
  const isValidIntent = validateRotationIntent({
    currentPiece: pieces[targetIndex],
    chosenDirection,
    validDirections: rotateResult.validDirections,
  });

  if (!isValidIntent) {
    await renderGrid(); // Clear UI feedback (arrows)
    return;
  }

  const startDir = pieces[targetIndex].direction;
  const endDir = chosenDirection;

  // Trigger the visual rotation before updating the service
  await animateRotation({
    coords,
    startDir,
    endDir,
    player,
  });

  // 2. Execute Rotation
  await commitRotation({
    targetIndex,
    currentPiece: pieces[targetIndex],
    chosenDirection,
  });

  // 3. Finalize Turn
  await finalizeRotateTurn({ coords, animateOnly, matchReceived });
};

/**
 * @brief Checks if the requested rotation is a valid change and permitted by rules.
 * @param {Object} params - Validation context.
 * @returns {boolean} True if the rotation should proceed.
 */
const validateRotationIntent = ({
  currentPiece,
  chosenDirection,
  validDirections,
}) => {
  // Check A: Did the direction actually change?
  if (currentPiece.direction === chosenDirection) {
    return false;
  }

  // Check B: Is the chosen direction allowed for this specific hex?
  if (!validDirections.includes(chosenDirection)) {
    return false;
  }

  return true;
};

/**
 * @brief Handles the post-rotation cleanup and turn switching.
 * @async
 */
const finalizeRotateTurn = async ({ coords, animateOnly, matchReceived }) => {
  if (animateOnly) {
    matchService.updateMatchInCache(matchReceived);
    board.render();
    mouse.clearDownEventInProgress();
    return;
  }

  // Reset force-rotate state and progress the turn
  matchService.setForceRotate(false);

  const changeTurnResult = await matchService.changeTurn(coords);
  if (changeTurnResult.status !== 200) {
    setUXAfterError();
    return;
  }

  await renderGrid();
};

/**
 * @brief Updates the piece data within the match service.
 * @async
 */
const commitRotation = async ({
  targetIndex,
  currentPiece,
  chosenDirection,
}) => {
  if (targetIndex < 0) return;

  const updatedPiece = {
    ...currentPiece,
    direction: chosenDirection,
  };

  await matchService.updatePiece({
    index: targetIndex,
    piece: updatedPiece,
  });
};

/**
 * @brief Orchestrates game actions based on player intent and match state.
 * @description Validates the match status and input coordinates before
 * dispatching the request to specific action handlers (push, rotate, remove).
 * @async
 * @param {Object} params - The action parameters.
 * @param {Object} params.coords - The hex coordinates targeted by the action.
 * @param {string} params.player - The player performing the action.
 * @param {boolean} [params.animateOnly] - If true, avoids database persistence.
 * @param {Object} [params.matchReceived] - Contextual match data for animations.
 */
const takeAction = async ({ coords, player, animateOnly, matchReceived }) => {
  // 1. Structural Guards
  if (!isActionAllowed(coords)) return;

  // 2. Dispatch to specialist handlers
  const actionType = matchService.getAction(player);

  const actionPayload = {
    coords,
    player,
    animateOnly,
    matchReceived,
  };

  switch (actionType) {
    case "push":
      await handlePush(actionPayload);
      break;
    case "rotate":
      await handleRotate(actionPayload);
      break;
    case "remove":
      await handleRemove(actionPayload);
      break;
    default:
      console.warn(`Unknown action type encountered: ${actionType}`);
  }
};

/**
 * @brief Performs comprehensive checks to see if an action can be processed.
 * @description Validates match state, animation concurrency, and coordinate integrity.
 * @param {Object} coords - The coordinates to validate.
 * @returns {boolean} True if the action is valid and can proceed.
 */
const isActionAllowed = (coords) => {
  const currentMatchResult = matchService.getCurrentMatch();

  // Check Match State
  if (currentMatchResult.currentMatch.state === "finished") {
    return false;
  }

  // Check Animation Concurrency
  if (isAnimationRunning()) {
    console.warn("Action blocked: Animation in progress.");
    return false;
  }

  // Validate Coordinates
  if (!isValidCoordinateInput(coords)) {
    return false;
  }

  return true;
};

/**
 * @brief Validates the format and presence of coordinate data.
 * @param {Object} coords - The coordinate object to check.
 * @returns {boolean} True if the object is a valid, non-empty coordinate set.
 */
const isValidCoordinateInput = (coords) => {
  if (coords === undefined || coords === null) {
    console.error("ERROR: takeAction input is missing.");
    return false;
  }

  if (typeof coords !== "object" || Object.keys(coords).length === 0) {
    console.error("ERROR: takeAction input is invalid or empty:", coords);
    return false;
  }

  return true;
};

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

const renderArrow = ({ centeredOnCoords, neighborCoords }) => {
  const mouseDownCanvasCoords = gameCoords2Pixels(centeredOnCoords); // center of hex that got mouse down
  const mouseHoverCanvasCoords = gameCoords2Pixels(neighborCoords); // center of hex that got mouse down
  const startX = mouseDownCanvasCoords.cx;
  const startY = mouseDownCanvasCoords.cy;
  const endX = mouseHoverCanvasCoords.cx;
  const endY = mouseHoverCanvasCoords.cy;
  const arrowHeadSize = zoom.getHexSide() / 2; // Length of the arrowhead wings
  const arrowHeadAngle = Math.PI / 6; // Angle of the arrowhead wings (e.g., 30 degrees)

  ctx.fillStyle = colors.getPlayerColor({
    player: matchService.getTurn().player,
    property: "hover",
  });
  ctx.lineWidth = HEX_LINE_WIDTH * 2;

  // ctx.stroke(); // Draw the line
  const angle = Math.atan2(endY - startY, endX - startX);

  // Calculate points for the arrowhead
  const arrowPoint1X = endX - arrowHeadSize * Math.cos(angle - arrowHeadAngle);
  const arrowPoint1Y = endY - arrowHeadSize * Math.sin(angle - arrowHeadAngle);

  const arrowPoint2X = endX - arrowHeadSize * Math.cos(angle + arrowHeadAngle);
  const arrowPoint2Y = endY - arrowHeadSize * Math.sin(angle + arrowHeadAngle);
  ctx.beginPath();
  ctx.moveTo(endX, endY);
  ctx.lineTo(arrowPoint1X, arrowPoint1Y);
  ctx.lineTo(arrowPoint2X, arrowPoint2Y);
  ctx.closePath(); // Close the path to form a triangle

  ctx.fill(); // Fill the arrowhead
};

/**
 * Draws a hexagon using these inputs:
 * * coords: game coordinates {s,q,r}
 * * borderColor: standard hex string for color
 * * fillColor: standard hex string for color
 * * to actually draw it, it translates to canvas coords and calls drawHexagonOnCanvas
 * @param {{coords:coords, borderColor:string, fillColor:string}} object
 * @returns {Object} {cx,cy} the center point in canvas coords of the hexagon
 */
const drawHexagonOnGrid = (params) => {
  const { coords, borderColor, fillColor } = params;

  const canvasCoords = gameCoords2Pixels(coords);
  let drawParams = {
    canvasCoords,
    borderColor,
    fillColor,
  };
  if (Object.keys(params).includes("validDirections")) {
    drawParams.validDirections = params.validDirections;
  }

  const result = drawHexagonOnCanvas(drawParams);
  return result;
};

const calculateAvailableSideColor = ({ i, validDirections, borderColor }) => {
  if (i === 1 && validDirections.includes("downRight")) {
    return borderColor;
  }
  if (i === 2 && validDirections.includes("down")) {
    return borderColor;
  }
  if (i === 3 && validDirections.includes("downLeft")) {
    return borderColor;
  }
  if (i === 4 && validDirections.includes("upLeft")) {
    return borderColor;
  }
  if (i === 5 && validDirections.includes("up")) {
    return borderColor;
  }
  if (i === 6 && validDirections.includes("upRight")) {
    return borderColor;
  }
  return colors.getGameColor("hexGrid");
};

/**
 * Draws the hexagon according to these keys in the params object:
 * * canvasCoords: {cx,cy} the center point of the hexagon in canvas coordinates
 * * borderColor: standard hex string for color
 * * fillColor: standard hex string for color
 * * validDirections: an array of directions. This is optional. If not provided, it renders all six side as available.
 * @param {params:{canvasCoords:canvasCoords, borderColor:string, fillColor:string, validDirections:array[string]}} object
 * @returns {object }{ cx, cy } e.g. the center points of the hexagon that was just drawn
 */
const drawHexagonOnCanvas = (params) => {
  let { canvasCoords, borderColor, fillColor } = params;
  const coords = pixels2gameCoords(canvasCoords);
  const isHoverHexagon = isHoveringHexagon(coords);
  const vertices = getHexagonVertices(canvasCoords.cx, canvasCoords.cy);
  vertices.push(vertices[0]); // 7 sets of coordinates, where the last ones are the same as the starting point

  // draw the outline
  for (let i = 1; i < vertices.length; i++) {
    switch (Object.keys(params).includes("validDirections")) {
      case true:
        const style = calculateAvailableSideColor({
          i,
          validDirections: params.validDirections,
          borderColor,
        });
        ctx.strokeStyle = style;
        break;
      case false:
        ctx.strokeStyle = borderColor;
        break;
    }
    ctx.beginPath();
    ctx.lineWidth = isHoverHexagon ? DIR_WIDTH : HEX_LINE_WIDTH;
    ctx.moveTo(vertices[i - 1].x, vertices[i - 1].y);
    ctx.lineTo(vertices[i].x, vertices[i].y);
    ctx.stroke();
    ctx.closePath();
  }

  // draw the outline one pixel and then fill it
  ctx.beginPath();
  const offset = HEX_LINE_WIDTH;
  ctx.moveTo(vertices[0].x - offset * 1.5, vertices[0].y);
  for (let i = 1; i < vertices.length; i++) {
    switch (true) {
      case i === 1:
        ctx.lineTo(vertices[i].x - offset, vertices[i].y - offset);
        break;
      case i === 2:
        ctx.lineTo(vertices[i].x + offset, vertices[i].y - offset);
        break;
      case i === 3:
        ctx.lineTo(vertices[i].x + offset * 1.5, vertices[i].y);
        break;
      case i === 4:
        ctx.lineTo(vertices[i].x + offset, vertices[i].y + offset);
        break;
      case i === 5:
        ctx.lineTo(vertices[i].x - offset, vertices[i].y + offset);
        break;
      case i === 6:
        ctx.lineTo(vertices[i].x - offset * 1.5, vertices[i].y);
        break;
      default:
        ctx.lineTo(vertices[i].x, vertices[i].y);
    }
  }
  if (fillColor === "none") {
    fillColor = "rgba(255, 255, 255, 0.0)";
  }
  ctx.fillStyle = fillColor;
  ctx.strokeStyle = fillColor;
  ctx.lineWidth = HEX_LINE_WIDTH;
  ctx.stroke();
  ctx.closePath();
  ctx.fill();
  return canvasCoords;
};

const q2r = ({ q, i }) => {
  const offset = Math.floor(q / 2);
  return i - offset;
};

const hexFrag = (frag) => {
  return zoom.getHexSide() * (frag / 100);
};

const drawDirectionOnGrid = ({ direction, color, coords }) => {
  const canvasCoords = gameCoords2Pixels(coords);
  drawDirectionOnCanvasNew({ canvasCoords, color, direction });
};

const getDirectionAngle = (direction) => {
  const angles = {
    up: 0,
    upRight: 60,
    downRight: 120,
    down: 180,
    downLeft: 240,
    upLeft: 300,
  };
  return angles[direction] ?? 0;
};

/**
 * Draws the direction indicator, supporting both standard strings and dynamic angles.
 * @param {Object} params
 * @param {Object} params.canvasCoords - {cx, cy} the center of the hexagon.
 * @param {string} params.color - The hex color string.
 * @param {string} [params.direction] - Optional: "up", "upRight", etc.
 * @param {number} [params.angle] - Optional: Rotation in degrees.
 */
/**
 * Draws a solid direction indicator.
 * Supports both standard direction strings and dynamic rotation angles.
 */
/**
 * Draws a "sleek" direction indicator with curved wings.
 */
/**
 * Draws a "sleek" direction indicator with a more pronounced curve.
 */
/**
 * Draws a "stealth" direction indicator with inward-curving wings.
 */
const drawDirectionOnCanvasNew = ({
  canvasCoords,
  color,
  direction,
  angle,
}) => {
  const { cx, cy } = canvasCoords;
  const rotationAngle =
    angle !== undefined ? angle : getDirectionAngle(direction);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((rotationAngle * Math.PI) / 180);

  ctx.beginPath();
  ctx.lineCap = "round";
  ctx.strokeStyle = color;
  ctx.lineWidth = DIR_WIDTH;

  // --- Right Wing (Bowing In) ---
  ctx.moveTo(0, -hexFrag(51));
  // Control point is now "inside" the triangle
  ctx.quadraticCurveTo(
    hexFrag(15),
    -hexFrag(17), // Pulling it toward the center
    hexFrag(32),
    0, // End point
  );

  // --- Left Wing (Bowing In) ---
  ctx.moveTo(0, -hexFrag(51));
  ctx.quadraticCurveTo(
    -hexFrag(15),
    -hexFrag(17), // Pulling it toward the center
    -hexFrag(31),
    0,
  );

  ctx.stroke();
  ctx.restore();
};

const storeHexagon = ({ cx, cy, s, q, r }) => {
  gameState.hexagons.push({ cx, cy, s, q, r });
};

const calcGridPlayerColor = ({ player, inAMouseDown, coords }) => {
  let fillColor;
  let dirColor;
  inAMouseDown
    ? (fillColor = colors.getPlayerColor({
        player: player,
        property: "piece",
      }))
    : (fillColor = colors.getPlayerColor({
        player: player,
        property: "pieceGray",
      }));
  inAMouseDown
    ? (dirColor = colors.getPlayerColor({
        player: player,
        property: "direction",
      }))
    : (dirColor = colors.getPlayerColor({
        player: player,
        property: "directionGray",
      }));
  // override the direction color if it's a double
  if (isDouble(coords)) {
    dirColor = colors.getGameColor("double");
  }
  return { fillColor, dirColor };
};

const drawTheGrid = () => {
  // clear the grid
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  // clear the hexagons array
  gameState.hexagons = [];

  const availables = [];
  for (let q = 0; q < GAME_BOARD_SIZE; q++) {
    for (let i = 0; i < GAME_BOARD_SIZE; i++) {
      const r = q2r({ q, i });
      const s = figureOutS(q, r);
      const coords = { s, q, r };
      // Since we are already iterating through
      // all the hexagons, this is the right place to
      // keep track of what's available

      const coordIsAvailable = isAvailable(coords);
      if (coordIsAvailable.available === true) {
        const availItem = { coords: coords };
        if (Object.keys(coordIsAvailable).includes("validDirections")) {
          availItem.validDirections = coordIsAvailable.validDirections;
        }
        availables.push(availItem);
      }

      const { player, direction } = gameCoordState(coords);
      const inAMouseDown = !Object.keys(gameState).includes("mouseDownCoords");

      let fillcolor;
      let dirColor;
      switch (player) {
        case "playerTwo":
          const resultP2 = calcGridPlayerColor({
            player,
            inAMouseDown,
            coords,
          });
          fillcolor = resultP2.fillColor;
          dirColor = isHoveringHexagon(coords)
            ? colors.getPlayerColor({ player: "playerTwo", property: "hover" })
            : resultP2.dirColor;
          break;
        case "playerOne":
          const resultP1 = calcGridPlayerColor({
            player,
            inAMouseDown,
            coords,
          });
          fillcolor = resultP1.fillColor;
          dirColor = isHoveringHexagon(coords)
            ? colors.getPlayerColor({ player: "playerOne", property: "hover" })
            : resultP1.dirColor;
          break;
        case "Empty":
          fillcolor = isDouble(coords)
            ? colors.getGameColor("double")
            : colors.getGameColor("background");
          dirColor = colors.getGameColor("background");
          break;
      }
      const params = {
        coords,
        borderColor: colors.getGameColor("hexGrid"),
        fillColor: fillcolor,
      };

      const { cx, cy } = drawHexagonOnGrid(params);
      if (isDouble(coords) && player === "Empty") {
        drawCenterText({ text: "Double", coords });
      }
      storeHexagon({ cx, cy, s, q, r });

      if (player != "Empty") {
        const params = {
          coords: coords,
          direction: direction,
          color: dirColor,
        };
        drawDirectionOnGrid(params);
      }
    }
  }

  return availables;
};

/**
 * Draws the availables
 * @param {{ borderColor: string, availables: availItem }}
 * * borderColor: string where string is a standard color hex or value
 * * availables is an array, where each availItem always has {coords:coords} coordinates.
 * * an availItem may have a key validDirections: that is an array strings indicating the rotations
 * it could rotate to
 * * OR an availItem may have a key movePlan: This is an array of pieces that would have to be moved.
 * @returns nothing
 */
const drawTheAvailables = ({ borderColor, availables }) => {
  for (const availItem of availables) {
    let params = {
      coords: availItem.coords,
      borderColor: borderColor,
      fillColor: "none",
    };
    if (Object.keys(availItem).includes("validDirections")) {
      params.validDirections = availItem.validDirections;
    }
    drawHexagonOnGrid(params);
  }
};

const drawTheHoveringHexagon = () => {
  if (gameState.hoveringHexagon.q > -1) {
    let color = hoverColor(isAvailable(gameState.hoveringHexagon).available);

    drawHexagonOnGrid({
      coords: gameState.hoveringHexagon,
      borderColor: color,
      fillColor: isDouble(gameState.hoveringHexagon)
        ? colors.getGameColor("double")
        : calculateFillColor(gameState.hoveringHexagon),
    });
    // we want the underneath direction arrow to be visible
    const underPiece = gameCoordState(gameState.hoveringHexagon);
    if (underPiece.player != "Empty") {
      drawDirectionOnGrid({
        coords: gameState.hoveringHexagon,
        color: colors.getPlayerColor({
          player: underPiece.player,
          property: "direction",
        }),
        direction: underPiece.direction,
      });
    }
  }
};

const renderGrid = async () => {
  // console.log("-----renderGrid:--------");
  // console.log("The timestamp is: " + new Date().toISOString());
  const currentMatchResult = matchService.getCurrentMatch();

  if (currentMatchResult.status != 200) {
    return;
  }
  const currentMatch = currentMatchResult.currentMatch;
  const availables = drawTheGrid();

  if (gameState.highlight.state === true) {
    drawHexagonOnGrid({
      coords: gameState.highlight.coords,
      borderColor: colors.getGameColor("iPop"),
      fillColor: colors.getGameColor("iPop"),
    });
  }

  if (currentMatch.active === "active") {
    drawTheAvailables({
      availables,
      borderColor: colors.getGameColor("hex_available"),
    });

    gameState.availables = availables;

    // Only players on the desktop get the hovering hexagon
    if (utilsApp.runningOnDesktop() && (await matchService.pAIsPlayer())) {
      drawTheHoveringHexagon();
    }
    // when nothing is available
    if (availables.length === 0) {
      switch (matchService.getAction(matchService.getTurn().player)) {
        case "push":
          matchService.setForceRotate(true); // since they can't add, it confusing if we allow them to change it to add
          await matchService.setAction({
            action: "rotate",
            player: matchService.getTurn().player,
          });
          // game could be over if they also can't rotate

          // The above already set the action to rotate, so now
          // we check if they can rotate
          const rotateAvailables = drawTheGrid();
          // and now we have to draw what's available:
          drawTheAvailables({
            availables: rotateAvailables,
            borderColor: colors.getGameColor("hex_available"),
          });
          gameState.availables = rotateAvailables;
          break;
        case "rotate":
          break;
      }
    }
  } // end match state check

  scoreBoard.updateScores(matchService.getPieces().pieces);
  scoreBoard.render();
  action.renderSection();
};

const calculateEndGame = async () => {
  // console.log("-----calculateEndGame:--------");
  // console.log("The timestamp is: " + new Date().toISOString());
  // console.log("The turn is " + matchService.getTurn().userID);

  const turn = matchService.getTurn();
  const pieces = matchService.getPieces().pieces; // returns a clone of the pieces array
  if (typeof pieces === "undefined") {
    return;
  }
  if (pieces.length === 0) {
    return;
  }

  var canPush = [];
  var canRotate = [];

  for (const piece of pieces) {
    canPush.push(isPushAvailable(piece.coords).available);
    canRotate.push(
      isRotateAvailable({ coords: piece.coords, turnAsPlayer: turn.player })
        .available,
    );
  }

  const pushAvailable = canPush.includes(true);
  const rotateAvailable = canRotate.includes(true);

  if (pushAvailable === false && rotateAvailable === false) {
    // The game is over when the player whose turn it is has no moves:

    const winner = getHighestScore(); //userID or "none"
    let namePart = "";
    if (winner === "none") {
      namePart = "a draw";
    }
    const currentMatch = matchService.getCurrentMatch().currentMatch;
    if (winner === currentMatch.p1UserID) {
      namePart = currentMatch.p1Name;
    }
    if (winner === currentMatch.p2UserID) {
      namePart = currentMatch.p2Name;
    }

    if (currentMatch.state != "finished") {
      document
        .getElementById("canvasContainer")
        .classList.remove("rotateCursor");
      const result = await matchService.setMatchState("finished");
      if (result.status != 200) {
        await matchService.setCurrentMatchID("noCurrentMatch");
        utilsApp.renderSessionTimeOutPop();
      }
      endGamePop.render();
    }
    render();
  }
};

const getAvailables = () => {
  return JSON.parse(JSON.stringify(gameState.availables)); //copy
};

const handlCanvasContextMenu = (event) => {
  event.preventDefault();
};

const getHoveringHexagon = () => {
  return gameState.hoveringHexagon;
};

const setHoveringHexagon = (coords) => {
  gameState.hoveringHexagon = coords;
};

eventHub.registerEvent("pointermove", "canvas", mouse.handleMouseMove);
eventHub.registerEvent("pointerout", "canvas", mouse.handleMouseLeave);
eventHub.registerEvent("pointerdown", "canvas", mouse.handleMouseDown);
eventHub.registerEvent("pointerup", "canvas", mouse.handleMouseUp);
eventHub.registerEvent("contextmenu", "canvas", handlCanvasContextMenu);

eventHub.registerEvent("pointerover", "rewind", mouse.handlePointerOver);
eventHub.registerEvent("pointerout", "rewind", mouse.handlePointerOut);
eventHub.registerEvent("click", "rewind", handleRewind);

eventHub.registerEvent("pointerover", "fastForward", mouse.handlePointerOver);
eventHub.registerEvent("pointerout", "fastForward", mouse.handlePointerOut);
eventHub.registerEvent("click", "fastForward", handleFastForward);

export const board = {
  render,
  renderArrow,
  calculateEndGame,
  drawHexagonOnGrid,
  drawHexagonOnCanvas,
  drawDirectionOnGrid,
  drawDirectionOnCanvasNew,
  drawTheAvailables,
  drawTheGrid,
  handleComputerTurn,
  renderGrid,
  getAvailables,
  getHoveringHexagon,
  getRewindOffset,
  setHighlightState,
  setHighlightCoords,
  setHoveringHexagon,
  takeAction,
};
